import React, { ReactNode } from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { formatAmount } from './formatAmount';
import { HedgeStatus } from './slidingScaleRows';

interface Props {
  travellers: { units: number; singles: number };
  linkedTravellers: number;
  hedges: Array<HedgeStatus>;
  /** Supplier cost with profit share, in the supplier's currency. */
  localCost: number | null;
  currency: string | null;
  audCost?: number;
  isConverting: boolean;
  isMissingCost: boolean;
}

function Tile({ label, tone, children }: { label: string; tone?: 'success'; children: ReactNode }) {
  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        p: 2.5,
        flex: '1 1 240px',
        minWidth: 240,
        borderRadius: 2,
        ...(tone === 'success' && {
          borderColor: alpha(theme.palette.success.main, 0.5),
          bgcolor: alpha(theme.palette.success.main, 0.06),
        }),
      })}
    >
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: 'block', lineHeight: 1.6, letterSpacing: 0.8 }}
      >
        {label}
      </Typography>
      {children}
    </Paper>
  );
}

function HedgeLine({ status, showName }: { status: HedgeStatus; showName: boolean }) {
  const isReached = status.paxSold >= status.hedgePax;
  const toGo = status.hedgePax - status.paxSold;

  return (
    <Box>
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography variant="h4" fontWeight={700}>
          {status.paxSold}
          <Typography component="span" variant="h6" color="text.secondary" fontWeight={500}>
            {' '}
            / {status.hedgePax} pax
          </Typography>
        </Typography>
        {showName && (
          <Typography variant="body2" color="text.secondary">
            {status.tourConfiguration}
          </Typography>
        )}
      </Stack>
      <LinearProgress
        variant="determinate"
        color={isReached ? 'success' : 'warning'}
        value={Math.min(100, (status.paxSold / status.hedgePax) * 100)}
        sx={{ height: 8, borderRadius: 4, my: 1 }}
      />
      <Stack direction="row" spacing={0.5} alignItems="center">
        {isReached && <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />}
        <Typography
          variant="body2"
          color={isReached ? 'success.main' : 'text.secondary'}
          fontWeight={isReached ? 600 : undefined}
        >
          {isReached ? 'Reached' : `${toGo} to go`}
        </Typography>
      </Stack>
    </Box>
  );
}

/** The departure at a glance: who is on it, how close it is to the hedge, and what it costs. */
export default function DepartureHeadline({
  travellers,
  linkedTravellers,
  hedges,
  localCost,
  currency,
  audCost,
  isConverting,
  isMissingCost,
}: Props) {
  const allReached = !!hedges.length && hedges.every((status) => status.paxSold >= status.hedgePax);
  const perTraveller = audCost !== undefined && travellers.units > 0 ? audCost / travellers.units : undefined;

  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
      <Tile label="Travellers">
        <Typography variant="h4" fontWeight={700}>
          {travellers.units}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {travellers.singles} single{travellers.singles === 1 ? '' : 's'}
          {linkedTravellers > 0 && ` · ${linkedTravellers} from LTLEs`}
        </Typography>
      </Tile>

      {!!hedges.length && (
        <Tile label="RM hedge" tone={allReached ? 'success' : undefined}>
          <Stack spacing={2}>
            {hedges.map((status) => (
              <HedgeLine key={status.tourConfiguration} status={status} showName={hedges.length > 1} />
            ))}
          </Stack>
        </Tile>
      )}

      <Tile label="Supplier cost">
        <Typography variant="h4" fontWeight={700}>
          {isConverting ? 'Converting…' : audCost === undefined ? '—' : `$${formatAmount(audCost)} AUD`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {localCost !== null && currency !== 'AUD' && `${formatAmount(localCost)} ${currency}`}
          {localCost !== null && currency !== 'AUD' && perTraveller !== undefined && ' · '}
          {perTraveller !== undefined && `$${formatAmount(perTraveller)} AUD per traveller`}
        </Typography>
        {isMissingCost && (
          <Typography variant="caption" color="warning.main" display="block">
            Some booked options have no cost price
          </Typography>
        )}
      </Tile>
    </Stack>
  );
}
